import { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, Store, Check, X, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { uploadImage } from "@/lib/upload";

const emptyForm = { name: "", logo_url: "" };

export const BrandsTab = () => {
  const [brands, setBrands] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  const load = () =>
    api.get("/brands").then((r) => setBrands(r.data)).catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editingId) {
        await api.put(`/admin/brands/${editingId}`, form);
        toast.success("Brand aggiornato");
      } else {
        await api.post("/admin/brands", form);
        toast.success("Brand aggiunto alla home");
      }
      setForm(emptyForm);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo brand?")) return;
    try {
      await api.delete(`/admin/brands/${id}`);
      toast.success("Brand eliminato");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const startEdit = (brand) => {
    setEditingId(brand.id);
    setForm({ name: brand.name, logo_url: brand.logo_url || "" });
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setForm((f) => ({ ...f, logo_url: url }));
      toast.success("Logo caricato");
    } catch (err) {
      toast.error(formatApiError(err, "Caricamento non riuscito."));
    } finally {
      setUploading(false);
    }
  };

  const inputCls =
    "rounded-xl border border-amber-500/25 bg-[#0A1D15]/80 px-4 py-2.5 text-sm text-[#FAF6EE] placeholder:text-emerald-100/40 outline-none focus:border-amber-400/70";

  return (
    <div className="space-y-8" data-testid="brands-tab">
      <form onSubmit={submit} className="flex flex-col gap-3 rounded-2xl border border-amber-500/25 bg-[#0C241A]/50 p-5 sm:flex-row sm:items-end">
        <div className="flex-1">
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-amber-300/80">Nome brand</label>
          <input
            data-testid="brand-name-input"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Es. Pasticceria del Corso"
            className={`w-full ${inputCls}`}
          />
        </div>
        <div className="flex-1">
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-amber-300/80">Logo (URL o caricato)</label>
          <div className="flex gap-2">
            <input
              data-testid="brand-logo-input"
              value={form.logo_url}
              onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
              placeholder="https://... oppure carica un file"
              className={`w-full ${inputCls}`}
            />
            <button
              type="button"
              data-testid="brand-logo-upload"
              disabled={uploading}
              onClick={() => fileInput.current?.click()}
              className="inline-flex shrink-0 items-center gap-2 rounded-xl border border-amber-400/40 px-4 py-2.5 text-sm font-semibold text-amber-200 hover:bg-amber-400/10 disabled:opacity-60"
            >
              <ImagePlus className="h-4 w-4" /> {uploading ? "..." : "Carica"}
            </button>
            <input
              type="file"
              accept="image/*"
              className="hidden"
              ref={fileInput}
              onChange={(e) => handleUpload(e.target.files?.[0])}
              data-testid="brand-logo-file"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {form.logo_url && (
            <img src={form.logo_url} alt="Logo" className="h-10 w-10 rounded-lg border border-amber-500/25 object-contain" />
          )}
          <button
            type="submit"
            data-testid="brand-submit-button"
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-600 px-5 py-2.5 text-sm font-bold text-[#1A1204] disabled:opacity-60"
          >
            {editingId ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            {editingId ? "Salva" : "Aggiungi"}
          </button>
          {editingId && (
            <button
              type="button"
              data-testid="brand-cancel-button"
              onClick={() => { setEditingId(null); setForm(emptyForm); }}
              className="rounded-xl border border-amber-500/30 px-4 py-2.5 text-sm text-emerald-100/70"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </form>

      <p className="text-sm text-emerald-100/60">
        I primi 8 brand appaiono negli spazi riservati della home page.
      </p>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="brands-grid">
        {brands.map((brand) => (
          <div
            key={brand.id}
            data-testid={`admin-brand-${brand.id}`}
            className="group rounded-2xl border border-amber-500/20 bg-[#0C241A]/60 p-5 text-center"
          >
            <div className="mx-auto flex h-14 items-center justify-center">
              {brand.logo_url ? (
                <img src={brand.logo_url} alt={brand.name} className="max-h-14 max-w-full object-contain" />
              ) : (
                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-amber-400/40 bg-amber-400/10 font-display text-xl font-bold text-amber-300">
                  {brand.name.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <p className="mt-3 truncate text-sm font-semibold text-amber-100">{brand.name}</p>
            <div className="mt-3 flex justify-center gap-2 opacity-0 transition-opacity group-hover:opacity-100">
              <button
                type="button"
                data-testid={`brand-edit-${brand.id}`}
                onClick={() => startEdit(brand)}
                className="rounded-lg border border-amber-400/40 p-2 text-amber-200 hover:bg-amber-400/10"
                aria-label="Modifica"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                data-testid={`brand-delete-${brand.id}`}
                onClick={() => remove(brand.id)}
                className="rounded-lg border border-red-400/40 p-2 text-red-300 hover:bg-red-400/10"
                aria-label="Elimina"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
        {brands.length === 0 && (
          <div className="col-span-full flex flex-col items-center gap-3 rounded-2xl border border-dashed border-amber-400/30 py-14 text-emerald-100/50">
            <Store className="h-8 w-8" />
            <p className="text-sm">Nessun brand ancora. Aggiungi il primo qui sopra.</p>
          </div>
        )}
      </div>
    </div>
  );
};
